import { useEffect, useState, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import { useQuery } from "@apollo/client";
import moment from "moment";
import { GROUP_DASHBOARD } from "../graphql/query/query"; // Update this import
import { useAuth } from "../context/Auth.context";
import { useAppContext } from "../context/App.context";
import HocPopup from "../hoc/hocPopup";
import CardComponent from "./app/home/manager/card/Card.component";
import DealershipTableComponent from "./app/home/manager/DealershipTable.component";
import Theme from "../utils/Theme";
import { CardStyle } from "./app/home/manager/card/Card.style";
import { HomeStyle } from "./app/home/Home.style";
import { capitalizeFirstLetter } from "../utils/Utils";

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const INITIAL_FILTER = {
  startDate: moment(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  ).format("MM/DD/YYYY"),
  endDate: moment(
    new Date().toLocaleString("en-US", { timeZone: userTimezone }),
  ).format("MM/DD/YYYY"),
};

const GroupDashboardComponent = ({ dialog }) => {
  const auth = useAuth();
  const app = useAppContext();

  const [filterData, setFilterData] = useState(INITIAL_FILTER);
  const [fetchInput, setFetchInput] = useState({
    employee_user_id: auth.user?.user_id, // Add this
    startDate: moment(filterData.startDate).format("YYYY-MM-DD"),
    endDate: moment(filterData.endDate).format("YYYY-MM-DD"),
    timezone: new Date().getTimezoneOffset(),
  });

  // Fetch group dashboard data
  const { data: groupData, loading } = useQuery(GROUP_DASHBOARD, {
    variables: {
      groupDashboardInput: {
        employee_user_id: auth.user?.user_id,
        d_startDate: fetchInput.startDate,
        d_endDate: fetchInput.endDate,
        timezone: fetchInput.timezone,
      },
    },
    skip: !auth.user?.user_id,
    fetchPolicy: "network-only",
  });

  console.log("groupData: ", groupData);

  // The backend returns an array of dealership data directly
  const dealershipData = useMemo(() => {
    // Check if groupDashboard returns an array directly
    const data = groupData?.groupDashboard?.groupDashboard || [];
    return Array.isArray(data) ? data : [];
  }, [groupData]);

  // Calculate consolidated KPIs
  const consolidatedKPI = useMemo(() => {
    const dealerships = dealershipData || [];

    return {
      completed: dealerships.reduce((sum, d) => sum + (d.completed || 0), 0),
      quotesRequested: dealerships.reduce(
        (sum, d) => sum + (d.quotesRequested || 0),
        0,
      ),
      review_provided: dealerships.reduce(
        (sum, d) => sum + (d.review_provided || 0),
        0,
      ),
      satisfied_yes: dealerships.reduce(
        (sum, d) => sum + (d.satisfied_yes || 0),
        0,
      ),
      satisfied_no: dealerships.reduce(
        (sum, d) => sum + (d.satisfied_no || 0),
        0,
      ),
      referral_provided: dealerships.reduce(
        (sum, d) => sum + (d.referral_provided || 0),
        0,
      ),
      apps_downloaded: dealerships.reduce(
        (sum, d) => sum + (d.apps_downloaded || 0),
        0,
      ),
      appointment_set: dealerships.reduce(
        (sum, d) => sum + (d.appointment_set || 0),
        0,
      ),
      watchedArtifacs: dealerships.reduce(
        (sum, d) => sum + (d.watchedArtifacs || 0),
        0,
      ),
      satisfiedRatio: `${dealerships.reduce((sum, d) => sum + (d.satisfied_yes || 0), 0)}/${dealerships.reduce((sum, d) => sum + (d.satisfied_no || 0), 0)}`,
      // NEW: consolidate the four "(+value)" follow-up counts across every
      // dealership, same reduce pattern used for everything else above.
      // These come straight from groupDashboard.js's per-dealership payload
      // (verified_followup / review_followup / referral_followup /
      // appointment_followup), which itself sums getDealershipUsers.util.js's
      // per-employee follow-up counts.
      verified_followup: dealerships.reduce(
        (sum, d) => sum + (d.verified_followup || 0),
        0,
      ),
      review_followup: dealerships.reduce(
        (sum, d) => sum + (d.review_followup || 0),
        0,
      ),
      referral_followup: dealerships.reduce(
        (sum, d) => sum + (d.referral_followup || 0),
        0,
      ),
      appointment_followup: dealerships.reduce(
        (sum, d) => sum + (d.appointment_followup || 0),
        0,
      ),
      // NEW: satisfied Y/N follow-up split (D = answer, F = second_review_answer)
      satisfied_yes_followup: dealerships.reduce(
        (sum, d) => sum + (d.satisfied_yes_followup || 0),
        0,
      ),
      satisfied_no_followup: dealerships.reduce(
        (sum, d) => sum + (d.satisfied_no_followup || 0),
        0,
      ),
    };
  }, [dealershipData]);

  const [checkService, setCheckService] = useState({
    insurance: true,
    knowledgeHub: false,
    prequal: false,
    idVerfication: false,
  });

  useEffect(() => {
    if (auth?.user?.dealershipServices?.length > 0) {
      auth?.user?.dealershipServices?.forEach((item) => {
        if (item?.service_id === 2)
          setCheckService((prev) => ({ ...prev, knowledgeHub: true }));
        if (item?.service_id === 3)
          setCheckService((prev) => ({ ...prev, prequal: true }));
        if (item?.service_id === 4)
          setCheckService((prev) => ({ ...prev, idVerfication: true }));
      });
    }
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
      }}
    >
      {/* KPI Cards */}
      <Box sx={HomeStyle.wrapper}>
        {/* First Card - Target & Completed */}
        <Box sx={CardStyle.wrapperBox}>
          <Paper sx={CardStyle.wrapperPaper}>
            <Box sx={CardStyle.content}>
              <Box sx={CardStyle.input}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body">Hello,</Typography>
                  <Typography variant="h7" sx={HomeStyle.hello}>
                    {capitalizeFirstLetter(auth.user?.first_name)}{" "}
                    {capitalizeFirstLetter(auth.user?.last_name)}
                  </Typography>
                </Box>
                <Box sx={CardStyle.iconMainBox}>
                  <Typography
                    textAlign="center"
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: Theme.palette.primary.main, fontSize: 16 }}
                  >
                    Group <br /> Dashboard
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider orientation="vertical" sx={{ m: "5px" }} />
            <Box sx={CardStyle.content}>
              <Box sx={CardStyle.input}>
                <Typography
                  textAlign="center"
                  sx={{
                    ...CardStyle.textInside,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                  {consolidatedKPI.completed} {/* Insurance Verified count */}
                  {/* NEW: re-verification count (verification_repull rows) */}
                  <Box
                    component="span"
                    sx={{
                      color: Theme.palette.primary.main,
                      fontSize: "0.65em",
                      fontWeight: 600,
                    }}
                  >
                    (+{consolidatedKPI.verified_followup ?? 0})
                  </Box>
                </Typography>
                <Typography
                  textAlign="center"
                  variant="body2"
                  sx={CardStyle.text}
                >
                  Insurance Verified
                </Typography>
                <Typography
                  textAlign="center"
                  sx={{
                    ...CardStyle.textInside,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                  {consolidatedKPI.review_provided}{" "}
                  {/* Reviews Provided = isReview rows + is_followup_review rows */}
                  {/* NEW: follow-up part only (is_followup_review rows),
                      already included in the total above */}
                  <Box
                    component="span"
                    sx={{
                      color: Theme.palette.primary.main,
                      fontSize: "0.65em",
                      fontWeight: 600,
                    }}
                  >
                    (+{consolidatedKPI.review_followup ?? 0})
                  </Box>
                </Typography>
                <Typography
                  textAlign="center"
                  variant="body2"
                  sx={CardStyle.text}
                >
                  Reviews Provided
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Second Card - Knowledge Hub & ID Verified */}
        <CardComponent
          leftLabel="Knowledge Hub Run"
          leftDisabled={!checkService?.knowledgeHub}
          leftValue={consolidatedKPI.watchedArtifacs}
          leftText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Box component="span">
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.65em",
                    fontWeight: 600,
                    mr: 0.3,
                  }}
                >
                  D:
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.75em",
                  }}
                >
                  {consolidatedKPI.satisfied_yes}
                  <Box
                    component="span"
                    sx={{
                      color: Theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    /{consolidatedKPI.satisfied_no}
                  </Box>
                </Box>
              </Box>

              <Box component="span">
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.65em",
                    fontWeight: 600,
                    mr: 0.3,
                  }}
                >
                  F:
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.75em",
                  }}
                >
                  {consolidatedKPI.satisfied_yes_followup}
                  <Box
                    component="span"
                    sx={{
                      color: Theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    /{consolidatedKPI.satisfied_no_followup}
                  </Box>
                </Box>
              </Box>
            </Box>
          }
          leftContent="Satisfied (Y/N)"
          rightLabel="ID Verified (P/F)"
          rightDisabled={!checkService?.idVerfication}
          rightValue={
            <Typography sx={CardStyle.textInside} textAlign="center">
              0
              <Box
                component="span"
                sx={{
                  color: Theme.palette.error.main,
                  fontSize: "0.95em",
                  fontWeight: 600,
                  ml: 0.5,
                }}
              >
                / 0
              </Box>
            </Typography>
          } // This field doesn't exist in GroupDashboardOutput
          rightText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <Box component="span">{consolidatedKPI.appointment_set}</Box>
              {/* NEW: service appointments booked via the follow-up
                  scheduling link (service_maintenance_vehicle rows) */}
              <Box
                component="span"
                sx={{
                  color: Theme.palette.primary.main,
                  fontSize: "0.65em",
                  fontWeight: 600,
                }}
              >
                (+{consolidatedKPI.appointment_followup ?? 0})
              </Box>
            </Box>
          }
          rightContent="1st Service Set"
        />

        {/* Third Card - PreQual Run & Quotes Requested */}
        <CardComponent
          leftLabel="PreQual Run"
          leftDisabled={!checkService?.prequal}
          leftValue={0} // This field doesn't exist in GroupDashboardOutput
          leftText={
            <Typography sx={CardStyle.textInside} textAlign="center">
              {consolidatedKPI.referral_provided}
              {/* NEW: referrals submitted via the follow-up referral link
                  (referral_response rows with is_followup = true) */}
              <Box
                component="span"
                sx={{
                  color: Theme.palette.primary.main,
                  fontSize: "0.65em",
                  fontWeight: 600,
                  ml: 0.5,
                }}
              >
                (+{consolidatedKPI.referral_followup ?? 0})
              </Box>
            </Typography>
          }
          leftContent="Referrals Provided"
          rightLabel="Quotes Requested"
          rightValue={consolidatedKPI.quotesRequested}
          rightText={consolidatedKPI.apps_downloaded}
          rightContent="Apps Downloaded"
        />
      </Box>

      {/* Dealership Table - Shows one row per dealership */}
      <DealershipTableComponent
        filterData={filterData}
        fetchInput={fetchInput}
        setFetchInput={setFetchInput}
        setFilterData={setFilterData}
        checkService={checkService}
        dealershipData={dealershipData}
        loading={loading}
        dialog={dialog}
        kpiData={consolidatedKPI}
        switchDealership={async (dealershipName) => {
          let dealership = auth?.user?.associateDetails.filter(
            (dealership) => dealership.dealership_name === dealershipName,
          );
          if (dealership && dealership[0]) {
            await auth.login({
              ...auth.user,
              dealership_id: dealership[0].dealership_id,
              dealership_name: dealership[0].dealership_name,
               brand_name: dealership[0].brand_name,
              dealership_code: dealership[0].dealership_code,
              payload: dealership[0].payload_preference,
              dealership_logo: dealership[0].dealership_logo,
              dealershipServices: dealership?.[0]?.dealershipServices,
            });
          }
        }}
      />
    </Box>
  );
};

export default HocPopup(GroupDashboardComponent);